import React, { useState } from 'react';
import {
  Box,
  Typography,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  FormControl,
  OutlinedInput,
  InputLabel,
  MenuItem,
  Select,
  Alert,
  CircularProgress,
  IconButton
} from '@mui/material';
import {
  PersonAdd as PersonAddIcon,
  CheckCircle as CheckCircleIcon,
  Delete as DeleteIcon,
  Add as AddIcon
} from '@mui/icons-material';
import { useMutation } from 'react-query';
import apiClient from '../../request/http-common';

const InviteMember = ({ open, onClose, jointAccountId, onSuccess }) => {
  const [members, setMembers] = useState([{ email: '', role: 'member' }]);
  const [currentStep, setCurrentStep] = useState('form'); // form, success

  const { mutate: inviteMember, isLoading: isInviting } = useMutation(
    async (memberData) => {
      const response = await apiClient.post(`/api/joint-accounts/${jointAccountId}/invite`, {
        email: memberData.email,
        role: memberData.role
      });
      return response.data;
    },
    {
      onSuccess: () => {
        setCurrentStep('success');
        onSuccess?.();
      },
      onError: (error) => {
        console.error('Member invitation failed:', error);
      }
    }
  );

  const handleMemberChange = (index, field, value) => {
    const newMembers = [...members];
    newMembers[index] = { ...newMembers[index], [field]: value };
    setMembers(newMembers);
  };

  const addMember = () => {
    setMembers([...members, { email: '', role: 'member' }]);
  };

  const removeMember = (index) => {
    if (members.length > 1) {
      setMembers(members.filter((_, i) => i !== index));
    }
  };

  const handleSubmit = async () => {
    const validMembers = members.filter(member => member.email && member.role);
    
    if (validMembers.length === 0) {
      return;
    }

    try {
      // Send invitations for all members
      for (const member of validMembers) {
        await inviteMember(member);
      }
    } catch (error) {
      console.error('Failed to send invitations:', error);
    }
  };

  const handleClose = () => {
    setCurrentStep('form');
    setMembers([{ email: '', role: 'member' }]);
    onClose();
  };

  const isFormValid = members.some(member => member.email && member.role);

  const roleDescriptions = {
    admin: 'Can approve transactions, invite members, and manage account settings',
    member: 'Can initiate transactions and view account details',
    viewer: 'Can only view account information and transaction history'
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          background: 'linear-gradient(135deg, #161A1E 0%, #1E2328 100%)',
          border: '1px solid #2B3139',
          borderRadius: '16px'
        }
      }}
    >
      <DialogTitle sx={{ 
        color: '#FFFFFF', 
        borderBottom: '1px solid #2B3139',
        display: 'flex',
        alignItems: 'center',
        gap: 2
      }}>
        <PersonAddIcon sx={{ color: '#10B981' }} />
        Invite Members to Joint Account
      </DialogTitle>

      <DialogContent sx={{ p: 3 }}>
        {currentStep === 'form' && (
          <Box>
            <Typography variant="h6" sx={{ color: '#FFFFFF', mb: 2 }}>
              Add New Members
            </Typography>
            <Typography variant="body2" sx={{ color: '#6C7293', mb: 3 }}>
              Invite new members to collaborate on this joint account. They will receive an email invitation.
            </Typography>

            {/* Add Member Button */}
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
              <Typography variant="subtitle1" sx={{ color: '#FFFFFF' }}>
                Member Invitations
              </Typography>
              <Button
                startIcon={<AddIcon />}
                onClick={addMember}
                variant="outlined"
                size="small"
                sx={{
                  borderColor: '#10B981',
                  color: '#10B981',
                  '&:hover': {
                    borderColor: '#059669',
                    backgroundColor: 'rgba(16, 185, 129, 0.1)'
                  }
                }}
              >
                Add Another
              </Button>
            </Box>

            {/* Member Forms */}
            {members.map((member, index) => (
              <Box 
                key={index} 
                sx={{ 
                  mb: 3, 
                  p: 2, 
                  border: '1px solid #2B3139', 
                  borderRadius: '8px',
                  background: 'linear-gradient(135deg, #1E2328 0%, #2B3139 100%)'
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                  <Typography variant="subtitle2" sx={{ color: '#FFFFFF', flex: 1 }}>
                    Member {index + 1}
                  </Typography>
                  {members.length > 1 && (
                    <IconButton
                      onClick={() => removeMember(index)}
                      size="small"
                      sx={{ color: '#F84960' }}
                    >
                      <DeleteIcon />
                    </IconButton>
                  )}
                </Box>

                <Box sx={{ 
                  display: 'flex', 
                  flexDirection: { xs: 'column', sm: 'row' },
                  gap: 2 
                }}>
                  <FormControl fullWidth>
                    <InputLabel sx={{ color: '#6C7293' }}>Email Address</InputLabel>
                    <OutlinedInput
                      value={member.email}
                      onChange={(e) => handleMemberChange(index, 'email', e.target.value)}
                      type="email"
                      placeholder="Enter email address"
                      sx={{
                        color: '#FFFFFF',
                        '& .MuiOutlinedInput-notchedOutline': {
                          borderColor: '#2B3139'
                        },
                        '&:hover .MuiOutlinedInput-notchedOutline': {
                          borderColor: '#F7931A'
                        }
                      }}
                    />
                  </FormControl>

                  <FormControl sx={{ minWidth: { xs: '100%', sm: 150 } }}>
                    <InputLabel sx={{ color: '#6C7293' }}>Role</InputLabel>
                    <Select
                      value={member.role}
                      onChange={(e) => handleMemberChange(index, 'role', e.target.value)}
                      sx={{
                        color: '#FFFFFF',
                        '& .MuiOutlinedInput-notchedOutline': {
                          borderColor: '#2B3139'
                        }
                      }}
                    >
                      <MenuItem value="admin">Admin</MenuItem>
                      <MenuItem value="member">Member</MenuItem>
                      <MenuItem value="viewer">Viewer</MenuItem>
                    </Select>
                  </FormControl>
                </Box>

                {member.role && (
                  <Typography variant="caption" sx={{ 
                    color: '#6C7293', 
                    display: 'block', 
                    mt: 1,
                    fontStyle: 'italic'
                  }}>
                    {roleDescriptions[member.role]}
                  </Typography>
                )}
              </Box>
            ))}

            {/* Role Information */}
            <Alert severity="info" sx={{ mb: 3 }}>
              <Typography variant="body2">
                <strong>Role Permissions:</strong><br/>
                • <strong>Admin:</strong> Full access except ownership transfer<br/>
                • <strong>Member:</strong> Can create transactions and view account<br/>
                • <strong>Viewer:</strong> Read-only access to account information
              </Typography>
            </Alert>

            <Box sx={{ display: 'flex', gap: 2 }}>
              <Button
                variant="outlined"
                onClick={handleClose}
                sx={{
                  borderColor: '#6C7293',
                  color: '#6C7293',
                  flex: 1
                }}
              >
                Cancel
              </Button>
              <Button
                variant="contained"
                onClick={handleSubmit}
                disabled={!isFormValid || isInviting}
                sx={{
                  background: 'linear-gradient(135deg, #10B981 0%, #059669 100%)',
                  flex: 1,
                  '&:disabled': {
                    background: '#2B3139'
                  }
                }}
              >
                {isInviting ? (
                  <>
                    <CircularProgress size={20} sx={{ mr: 1, color: '#FFFFFF' }} />
                    Sending Invitations...
                  </>
                ) : (
                  `Send ${members.filter(m => m.email).length} Invitation${members.filter(m => m.email).length !== 1 ? 's' : ''}`
                )}
              </Button>
            </Box>
          </Box>
        )}

        {currentStep === 'success' && (
          <Box sx={{ textAlign: 'center' }}>
            <CheckCircleIcon sx={{ fontSize: 64, color: '#10B981', mb: 2 }} />
            <Typography variant="h6" sx={{ color: '#FFFFFF', mb: 2 }}>
              Invitations Sent Successfully
            </Typography>
            <Typography variant="body2" sx={{ color: '#6C7293', mb: 3 }}>
              Email invitations have been sent to all specified members. They will receive an email 
              with instructions to accept or decline the invitation.
            </Typography>
            
            <Box sx={{
              background: 'linear-gradient(135deg, #1E2328 0%, #2B3139 100%)',
              border: '1px solid #3B4148',
              borderRadius: '8px',
              p: 2,
              mb: 3,
              textAlign: 'left'
            }}>
              <Typography variant="subtitle2" sx={{ color: '#FFFFFF', mb: 1 }}>
                Invited Members:
              </Typography>
              {members.filter(m => m.email).map((member, index) => (
                <Box key={index} sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                  <Typography variant="body2" sx={{ color: '#6C7293' }}>
                    {member.email}
                  </Typography>
                  <Typography variant="body2" sx={{ color: '#F7931A', fontWeight: 600 }}>
                    {member.role.charAt(0).toUpperCase() + member.role.slice(1)}
                  </Typography>
                </Box>
              ))}
            </Box>

            <Typography variant="body2" sx={{ color: '#6C7293', mb: 3 }}>
              Invited members will appear in the Members tab once they accept their invitations.
            </Typography>

            <Button
              variant="contained"
              onClick={handleClose}
              sx={{
                background: 'linear-gradient(135deg, #F7931A 0%, #FFB800 100%)'
              }}
            >
              Close
            </Button>
          </Box>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default InviteMember; 